import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import orangeSortIcon from "../media/orange-sort-icon.svg";
import deleteButtonIcon from "../media/delete-button.svg";
import docButton from "../media/doc-orange.svg";
import jpgPhotoButton from "../media/jpg-photo.svg";
import pdfButton from "../media/orange-PDF.svg";
import txtButton from "../media/txt-orange.svg";
import xlsButton from "../media/xls-orange.svg";
import zipButton from "../media/zip.svg";
import mpegVideoButton from "../media/mpeg-video.svg";
import JSZip from "jszip";
import UploadProgressBar from "./progress/UploadProgressBar";
import RichTextEditor from "./richTextEditor/richTextEditor";

const videoExtensions = ["mp4", "avi", "mov", "mkv", "mpeg", "mpg"];

function getIconWithPathForFile(fileName) {
  if (!fileName) return null;
  const fileExtension = fileName.split(".").pop().toLowerCase();
  if (videoExtensions.includes(fileExtension)) return mpegVideoButton;
  switch (fileExtension) {
    case "jpg":
    case "jpeg":
    case "png":
    case "svg":
      return jpgPhotoButton;
    case "pdf":
      return pdfButton;
    case "doc":
    case "docx":
      return docButton;
    case "xls":
    case "xlsx":
      return xlsButton;
    case "txt":
      return txtButton;
    case "zip":
      return zipButton;
    default:
      return txtButton;
  }
}

function getIconForFile(fileName) {
  if (!fileName) return "txtButton";
  const fileExtension = fileName.split(".").pop().toLowerCase();
  if (videoExtensions.includes(fileExtension)) return "mpegVideoButton";
  switch (fileExtension) {
    case "jpg":
    case "jpeg":
    case "png":
    case "svg":
      return "jpgPhotoButton";
    case "pdf":
      return "pdfButton";
    case "doc":
    case "docx":
      return "docButton";
    case "xls":
    case "xlsx":
      return "xlsButton";
    case "txt":
      return "txtButton";
    case "zip":
      return "zipButton";
    default:
      return "txtButton";
  }
}

const BenefitDocumentUpload = ({
  titleLabel,
  descriptionLabel,
  filesLabel,
  submitLabel,
  onSuccess,
  onError,
  apiPath,
  subCategory,
  category,
  navigateTo,
  RichTextEditorComponent = RichTextEditor,
  UploadProgressBarComponent = UploadProgressBar,
  defaultTitle = "",
  defaultDescription = "",
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState(defaultDescription);
  const [files, setFiles] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [hover, setHover] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const navigate = typeof navigateTo === "function" ? navigateTo : () => {};

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || files.length === 0) {
      onError && onError(t("documentEditor.errorTitleRequired"));
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    formData.append("title", title);
    formData.append("name", title);
    formData.append("description", description);
    formData.append("category", category);
    formData.append("subCategory", subCategory);
    if (files.length > 1) {
      const zip = new JSZip();
      files.forEach((file) => zip.file(file.name, file));
      const zipFileName = `dokumenty-${Date.now()}.zip`;
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipFile = new File([zipBlob], zipFileName, {
        type: "application/zip",
      });
      formData.append("file", zipFile);
      formData.append("icon", "zipButton");
    } else {
      formData.append("file", files[0]);
      const iconName = getIconForFile(files[0].name);
      formData.append("icon", iconName);
    }
    try {
      await apiPath(formData, setUploadProgress);
      setTitle("");
      setDescription("");
      setFiles([]);
      setSuccessMessage(t("documentEditor.successAdd"));
      onSuccess && onSuccess();
      setTimeout(() => {
        navigate();
      }, 500);
    } catch (error) {
      onError && onError(t("documentEditor.errorAddFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const globalIcon =
    files.length > 1
      ? zipButton
      : files.length === 1
        ? getIconWithPathForFile(files[0].name)
        : null;

  return (
    <div className="add-announcement-container">
      <div className="main-site">
        <h1>{submitLabel}</h1>
        <div
          className="orange-sort-icon"
          onClick={navigate}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hover ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>
      <form onSubmit={handleSubmit} className="add-announcement-form">
        <div className="form-group">
          <label htmlFor="title">
            {titleLabel || t("documentEditor.documentTitle")}
          </label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("documentEditor.enterTitle")}
          />
        </div>
        <div className="form-group">
          <label htmlFor="description">
            {descriptionLabel || t("documentEditor.documentDescription")}
          </label>
          <RichTextEditorComponent
            content={description}
            onChange={setDescription}
          />
        </div>
        <div className="form-group">
          <label htmlFor="files">
            {filesLabel || t("documentEditor.selectDocuments")}
          </label>
          <label htmlFor="files" className="custom-file-upload">
            {t("documentEditor.addFiles")}
          </label>
          <input
            type="file"
            id="files"
            multiple
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </div>
        {files.length > 0 && (
          <div className="selected-files">
            <h4>{t("documentEditor.attachedFiles")}</h4>
            <div className="flex-section">
              <div className="global-icon">
                <img
                  src={globalIcon}
                  alt="Globalna ikona pliku"
                  className="file-icon"
                />
              </div>
              <ul>
                {files.map((file, index) => (
                  <li key={index} className="file-item">
                    <span>{file.name}</span>
                    <button
                      type="button"
                      className="remove-file"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <img
                        src={deleteButtonIcon}
                        alt={t("documentEditor.remove")}
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {isLoading && <UploadProgressBarComponent progress={uploadProgress} />}
        <button type="submit" className="submit-button" disabled={isLoading}>
          {submitLabel}
        </button>
      </form>
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
    </div>
  );
};

export default BenefitDocumentUpload;
